import {
  Component,
  signal,
  ChangeDetectionStrategy,
  output,
  input,
  viewChild,
  HostListener,
  effect,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { Popover, PopoverModule } from 'primeng/popover';
import {
  QueryAuditLogsEventTypeEnum,
  QueryAuditLogsScopeTypeEnum,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import {
  CloseOverlayOnNavigationDirective,
  SettingsListHeader,
} from '@tourfold/libraries/shared-ui';

export interface AuditLogFilterValues {
  fromDate: Date | null;
  toDate: Date | null;
  eventType: QueryAuditLogsEventTypeEnum | null;
  scopeType: QueryAuditLogsScopeTypeEnum | null;
  scopeId: string;
  actorId: string;
}

@Component({
  selector: 'tf-settings-audit-log-filters',
  imports: [
    FormsModule,
    DatePickerModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    PopoverModule,
    TranslateModule,
    TooltipModule,
    SettingsListHeader,
    CloseOverlayOnNavigationDirective,
  ],
  templateUrl: './audit-log-filters.html',
  styleUrl: './audit-log-filters.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogFilters {
  // Inputs
  readonly fromDate = input<Date | null>(null);
  readonly toDate = input<Date | null>(null);
  readonly eventType = input<QueryAuditLogsEventTypeEnum | null>(null);
  readonly scopeType = input<QueryAuditLogsScopeTypeEnum | null>(null);
  readonly scopeId = input<string>('');
  readonly actorId = input<string>('');
  readonly hasActiveFilters = input<boolean>(false);
  readonly isLoading = input<boolean>(false);
  // Outputs
  readonly filterApplied = output<AuditLogFilterValues>();
  readonly filterCleared = output();
  readonly reloadAuditLogs = output();
  // View children
  readonly filterPopover = viewChild<Popover>('filterPopover');

  // Local state
  readonly localFromDate = signal<Date | null>(null);
  readonly localToDate = signal<Date | null>(null);
  readonly localEventType = signal<QueryAuditLogsEventTypeEnum | null>(null);
  readonly localScopeType = signal<QueryAuditLogsScopeTypeEnum | null>(null);
  readonly localScopeId = signal<string>('');
  readonly localActorId = signal<string>('');
  readonly viewportWidth = signal(window.innerWidth);

  readonly eventTypeOptions = Object.values(QueryAuditLogsEventTypeEnum).map((type) => ({
    label: type.replace(/_/g, ' '),
    value: type,
  }));

  readonly scopeTypeOptions = Object.values(QueryAuditLogsScopeTypeEnum).map((type) => ({
    label: type.replace(/_/g, ' '),
    value: type,
  }));

  constructor() {
    // Sync input signals to local signals
    effect(() => {
      this.localFromDate.set(this.fromDate());
      this.localToDate.set(this.toDate());
      this.localEventType.set(this.eventType());
      this.localScopeType.set(this.scopeType());
      this.localScopeId.set(this.scopeId());
      this.localActorId.set(this.actorId());
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewportWidth.set(window.innerWidth);
  }

  get isDesktopView(): boolean {
    return this.viewportWidth() >= 1600;
  }

  get isTabletView(): boolean {
    return this.viewportWidth() < 1600;
  }

  onFromDateChange(date: Date | null): void {
    this.localFromDate.set(date);
  }

  onToDateChange(date: Date | null): void {
    this.localToDate.set(date);
  }

  onEventTypeChange(eventType: QueryAuditLogsEventTypeEnum | null): void {
    this.localEventType.set(eventType);
  }

  onScopeTypeChange(scopeType: QueryAuditLogsScopeTypeEnum | null): void {
    this.localScopeType.set(scopeType);
  }

  onScopeIdChange(scopeId: string): void {
    this.localScopeId.set(scopeId);
  }

  onActorIdChange(actorId: string): void {
    this.localActorId.set(actorId);
  }

  applyFilters(): void {
    this.filterApplied.emit({
      fromDate: this.localFromDate(),
      toDate: this.localToDate(),
      eventType: this.localEventType(),
      scopeType: this.localScopeType(),
      scopeId: this.localScopeId(),
      actorId: this.localActorId(),
    });
    this.filterPopover()?.hide();
  }

  clearFilters(): void {
    this.localFromDate.set(null);
    this.localToDate.set(null);
    this.localEventType.set(null);
    this.localScopeType.set(null);
    this.localScopeId.set('');
    this.localActorId.set('');
    this.filterCleared.emit();
    this.filterPopover()?.hide();
  }

  toggleFilterPopover(event: Event): void {
    this.filterPopover()?.toggle(event);
  }
}
